import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, mediaUrl } from "@/lib/api";
import { uploadFile } from "@/lib/upload";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Trash2 } from "lucide-react";

const FIELDS = ["diet", "exercise", "reading", "reflection"];
const PHASE_NAME = { pre: "Pre-Pregnancy", preg: "Pregnancy", post: "Post-Pregnancy" };
const AUDIENCES = [
  { v: "all", label: "All active participants" },
  { v: "phase:pre", label: "Pre-Pregnancy only" },
  { v: "phase:preg", label: "Pregnancy only" },
  { v: "phase:post", label: "Post-Pregnancy only" },
  { v: "trimester:1", label: "Trimester 1 (months 1–3)" },
  { v: "trimester:2", label: "Trimester 2 (months 4–6)" },
  { v: "trimester:3", label: "Trimester 3 (months 7–9)" },
];
const toAudience = (v) => {
  const [kind, val] = v.split(":");
  return kind === "all" ? { type: "all" } : kind === "phase" ? { type: "phase", phase: val } : { type: "trimester", trimester: Number(val) };
};
const Select2 = ({ value, onChange, options, testid }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
    className="h-10 rounded-md border border-border bg-transparent px-3 text-sm w-full">
    {options.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
  </select>
);

const ContentEditor = () => {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({});
  const [open, setOpen] = useState("");
  const load = async () => {
    const { data: body } = await api.get("/garbha/admin/content");
    setData(body);
    setDraft({ ...body.meta, ...Object.fromEntries(body.stages.map((s) => [s.key, s])) });
  };
  useEffect(() => { load(); }, []);
  if (!data) return <p className="text-sm text-muted-foreground">Loading…</p>;

  const setStage = (key, patch) => setDraft((d) => ({ ...d, [key]: { ...d[key], ...patch } }));
  const saveStage = async (key) => {
    try {
      await api.put(`/garbha/admin/content/${key}`, FIELDS.concat("image").reduce((a, f) => ({ ...a, [f]: draft[key][f] || "" }), {}));
      toast.success("Month saved");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveMeta = async () => {
    try {
      await api.put("/garbha/admin/meta", { values: Object.fromEntries(data.meta_fields.map((f) => [f, draft[f] || ""])) });
      toast.success("Page content saved");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const upload = async (e, apply) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const { url } = await uploadFile(file, "branding", { maxWidth: 1600 });
      apply(url);
      toast.success("Image uploaded — press Save");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <Card className="border-border"><CardContent className="p-6 space-y-4">
        <h4 className="font-display text-2xl">Landing page, pricing note & the two stories</h4>
        {data.meta_fields.map((f) => (
          <div key={f} className="grid lg:grid-cols-[200px_1fr] gap-3 items-start">
            <Label className="overline">{f.replace(/_/g, " ")}</Label>
            {f.includes("image") ? (
              <div className="flex flex-wrap items-center gap-3">
                {draft[f] && <img src={mediaUrl(draft[f])} alt="" className="w-24 h-16 rounded-lg object-cover border border-border" />}
                <label className="btn-pill border border-border px-3 py-1.5 text-xs cursor-pointer">Replace
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e, (u) => setDraft((d) => ({ ...d, [f]: u })))} data-testid={`garbha-meta-upload-${f}`} />
                </label>
                <Input value={draft[f] || ""} onChange={(e) => setDraft((d) => ({ ...d, [f]: e.target.value }))} className="bg-transparent flex-1 min-w-[180px]" data-testid={`garbha-meta-${f}`} />
              </div>
            ) : f.includes("body") || f.includes("subtitle") || f === "disclaimer" ? (
              <Textarea rows={f.includes("body") ? 7 : 2} value={draft[f] || ""} onChange={(e) => setDraft((d) => ({ ...d, [f]: e.target.value }))} className="bg-transparent" data-testid={`garbha-meta-${f}`} />
            ) : (
              <Input value={draft[f] || ""} onChange={(e) => setDraft((d) => ({ ...d, [f]: e.target.value }))} className="bg-transparent" data-testid={`garbha-meta-${f}`} />
            )}
          </div>
        ))}
        <Button onClick={saveMeta} className="btn-pill bg-primary text-primary-foreground h-10" data-testid="garbha-meta-save">Save page content</Button>
      </CardContent></Card>

      {data.stages.map((s) => (
        <Card key={s.key} className="border-border"><CardContent className="p-5">
          <button className="flex w-full items-center justify-between" onClick={() => setOpen(open === s.key ? "" : s.key)} data-testid={`garbha-stage-toggle-${s.key}`}>
            <span className="font-display text-xl">{PHASE_NAME[s.phase]} · Month {s.month}</span>
            <Badge variant="outline" className="text-[10px]">{s.key}</Badge>
          </button>
          {open === s.key && (
            <div className="mt-5 space-y-3">
              {FIELDS.map((f) => (
                <div key={f}>
                  <Label className="overline capitalize">{f}</Label>
                  <Textarea rows={3} value={draft[s.key]?.[f] || ""} onChange={(e) => setStage(s.key, { [f]: e.target.value })} className="mt-2 bg-transparent" data-testid={`garbha-${s.key}-${f}`} />
                </div>
              ))}
              <div className="flex flex-wrap items-center gap-3">
                {draft[s.key]?.image && <img src={mediaUrl(draft[s.key].image)} alt="" className="w-20 h-14 rounded-lg object-cover border border-border" />}
                <label className="btn-pill border border-border px-3 py-1.5 text-xs cursor-pointer">Month image
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e, (u) => setStage(s.key, { image: u }))} data-testid={`garbha-${s.key}-upload`} />
                </label>
                <Button onClick={() => saveStage(s.key)} className="btn-pill bg-primary text-primary-foreground h-10 ml-auto" data-testid={`garbha-${s.key}-save`}>Save month</Button>
              </div>
            </div>
          )}
        </CardContent></Card>
      ))}
    </div>
  );
};

const Participants = () => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", phase: "preg", month: 1, status: "active", notes: "" });
  const [detail, setDetail] = useState(null);
  const load = async () => setData((await api.get("/garbha/admin/participants")).data);
  useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      await api.post("/garbha/admin/participants", { ...form, month: Number(form.month) });
      toast.success("Participant added");
      setForm({ ...form, name: "", email: "", phone: "", notes: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const patch = async (p, changes) => {
    try {
      await api.put(`/garbha/admin/participants/${p.id}`, { ...p, ...changes, month: Number(changes.month ?? p.month) });
      toast.success("Updated");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/garbha/admin/participants/${id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (!data) return <p className="text-sm text-muted-foreground">Loading…</p>;

  if (detail) {
    const p = detail.participant;
    return (
      <Card className="border-border" data-testid="garbha-participant-detail"><CardContent className="p-6 space-y-4">
        <button onClick={() => setDetail(null)} className="text-xs text-muted-foreground hover:text-primary" data-testid="garbha-detail-back">← All participants</button>
        <h4 className="font-display text-2xl">{p.name}</h4>
        <p className="text-sm text-muted-foreground">{p.email} · {p.phone} · joined {new Date(p.joined_at).toLocaleDateString()}</p>
        <p className="text-sm">Now at <strong>{PHASE_NAME[p.phase]} · Month {p.month}</strong> · {p.status} · payment {detail.subscription?.status || "—"}</p>
        <div>
          <Label className="overline">Notes about this participant</Label>
          <Textarea rows={3} defaultValue={p.notes} onBlur={(e) => patch(p, { notes: e.target.value })} className="mt-2 bg-transparent" data-testid="garbha-detail-notes" />
        </div>
        <div>
          <div className="overline mb-2">Progress history</div>
          {p.history.map((h, i) => (
            <div key={i} className="text-xs text-muted-foreground">{new Date(h.at).toLocaleString()} — {PHASE_NAME[h.phase]} month {h.month} ({h.by})</div>
          ))}
        </div>
        <div className="text-xs text-muted-foreground">{detail.samvad.length} Samvad invitation(s) · {detail.messages.length} announcement(s)</div>
      </CardContent></Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="garbha-participants">
      <Card className="border-border"><CardContent className="p-6">
        <h4 className="font-display text-2xl mb-4">Add a participant</h4>
        <div className="grid md:grid-cols-3 gap-3">
          <Input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-transparent" data-testid="garbha-p-name" />
          <Input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-transparent" data-testid="garbha-p-email" />
          <Input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-transparent" data-testid="garbha-p-phone" />
          <Select2 value={form.phase} onChange={(v) => setForm({ ...form, phase: v, month: 1 })} testid="garbha-p-phase"
            options={data.phases.map((p) => ({ v: p.key, label: p.title }))} />
          <Input type="number" min="1" value={form.month} onChange={(e) => setForm({ ...form, month: e.target.value })} className="bg-transparent" data-testid="garbha-p-month" />
          <Button onClick={save} className="btn-pill bg-primary text-primary-foreground h-10" data-testid="garbha-p-save">Add participant</Button>
        </div>
      </CardContent></Card>

      <p className="text-sm text-muted-foreground">{data.participants.length} enrolled · {data.active} active</p>
      {data.participants.map((p) => (
        <Card key={p.id} className="border-border" data-testid={`garbha-p-row-${p.id}`}><CardContent className="p-5 flex flex-wrap items-center gap-3">
          <button onClick={() => api.get(`/garbha/admin/participants/${p.id}`).then(({ data }) => setDetail(data))} className="text-left" data-testid={`garbha-p-open-${p.id}`}>
            <div className="font-display text-lg hover:text-primary">{p.name}</div>
            <div className="text-xs text-muted-foreground">{p.email || "no email"} · {p.phone || "no phone"} · joined {new Date(p.joined_at).toLocaleDateString()}</div>
          </button>
          <div className="flex items-center gap-2 ml-auto">
            <Select2 value={p.phase} onChange={(v) => patch(p, { phase: v, month: 1 })} testid={`garbha-p-phase-${p.id}`}
              options={data.phases.map((x) => ({ v: x.key, label: x.title }))} />
            <Input type="number" min="1" defaultValue={p.month} onBlur={(e) => patch(p, { month: e.target.value })} className="bg-transparent w-20" data-testid={`garbha-p-month-${p.id}`} />
            <Select2 value={p.status} onChange={(v) => patch(p, { status: v })} testid={`garbha-p-status-${p.id}`}
              options={[{ v: "active", label: "Active" }, { v: "paused", label: "Paused" }, { v: "cancelled", label: "Cancelled" }]} />
            <button onClick={() => remove(p.id)} className="text-muted-foreground hover:text-destructive" data-testid={`garbha-p-delete-${p.id}`}><Trash2 size={15} /></button>
          </div>
        </CardContent></Card>
      ))}
    </div>
  );
};

const Scheduler = ({ kind }) => {
  const isSamvad = kind === "samvad";
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ title: "", topic: "", scheduled_at: "", session_link: "", body: "", image: "", audience: "all" });
  const load = async () => {
    const { data } = await api.get(`/garbha/admin/${isSamvad ? "samvad" : "messages"}`);
    setRows(isSamvad ? data.samvad : data.messages);
  };
  useEffect(() => { load(); }, [kind]); // eslint-disable-line
  const create = async () => {
    try {
      const payload = isSamvad
        ? { title: f.title, topic: f.topic, scheduled_at: f.scheduled_at, session_link: f.session_link, audience: toAudience(f.audience) }
        : { title: f.title, body: f.body, image: f.image, audience: toAudience(f.audience) };
      await api.post(`/garbha/admin/${isSamvad ? "samvad" : "messages"}`, payload);
      toast.success(isSamvad ? "Samvad scheduled" : "Message sent");
      setF({ ...f, title: "", topic: "", scheduled_at: "", body: "", image: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const pickImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const { url } = await uploadFile(file, "branding", { maxWidth: 1200 });
      setF((v) => ({ ...v, image: url }));
      toast.success("Image attached");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/garbha/admin/${isSamvad ? "samvad" : "messages"}/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4" data-testid={`garbha-${kind}`}>
      <Card className="border-border"><CardContent className="p-6 space-y-3">
        <h4 className="font-display text-2xl">{isSamvad ? "Schedule a Samvad" : "Send an announcement"}</h4>
        <Input placeholder="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="bg-transparent" data-testid={`${kind}-title`} />
        {isSamvad ? (
          <>
            <Input placeholder="Topic" value={f.topic} onChange={(e) => setF({ ...f, topic: e.target.value })} className="bg-transparent" data-testid="samvad-topic" />
            <Input type="datetime-local" value={f.scheduled_at} onChange={(e) => setF({ ...f, scheduled_at: e.target.value })} className="bg-transparent" data-testid="samvad-when" />
            <Input placeholder="Zoom / Google Meet link" value={f.session_link} onChange={(e) => setF({ ...f, session_link: e.target.value })} className="bg-transparent" data-testid="samvad-link" />
          </>
        ) : (
          <>
            <Textarea rows={4} placeholder="Message to participants" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} className="bg-transparent" data-testid="message-body" />
            <div className="flex flex-wrap items-center gap-3">
              {f.image && <img src={mediaUrl(f.image)} alt="" className="w-20 h-14 rounded-lg object-cover border border-border" />}
              <label className="btn-pill border border-border px-3 py-1.5 text-xs cursor-pointer">
                {f.image ? "Change image" : "Attach an image (optional)"}
                <input type="file" accept="image/*" className="hidden" onChange={pickImage} data-testid="message-image" />
              </label>
              {f.image && (
                <button onClick={() => setF({ ...f, image: "" })} className="text-xs text-muted-foreground hover:text-destructive" data-testid="message-image-clear">
                  Remove
                </button>
              )}
            </div>
          </>
        )}
        <Select2 value={f.audience} onChange={(v) => setF({ ...f, audience: v })} options={AUDIENCES} testid={`${kind}-audience`} />
        <Button onClick={create} className="btn-pill bg-primary text-primary-foreground h-10" data-testid={`${kind}-save`}>
          {isSamvad ? "Schedule it" : "Send now"}
        </Button>
      </CardContent></Card>

      {rows.length === 0 && <p className="text-sm text-muted-foreground" data-testid={`${kind}-empty`}>Nothing yet.</p>}
      {rows.map((r) => (
        <Card key={r.id} className="border-border" data-testid={`${kind}-row-${r.id}`}><CardContent className="p-5 flex flex-wrap items-center gap-3">
          <div>
            <div className="font-medium">{r.title}</div>
            <div className="text-xs text-muted-foreground">
              {isSamvad
                ? `${new Date(r.scheduled_at).toLocaleString()}${r.topic ? ` · ${r.topic}` : ""} · ${r.audience_size} invited`
                : `${new Date(r.sent_at).toLocaleString()} · ${r.recipients} recipient(s)`}
              {" · "}{r.audience?.type === "all" ? "everyone" : r.audience?.phase || `trimester ${r.audience?.trimester}`}
              {isSamvad && new Date(r.scheduled_at) < new Date() && (
                <span className="text-secondary"> · already past, participants no longer see it</span>
              )}
            </div>
            {!isSamvad && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{r.body}</p>}
          </div>
          <button onClick={() => remove(r.id)} className="ml-auto text-muted-foreground hover:text-destructive" data-testid={`${kind}-delete-${r.id}`}><Trash2 size={15} /></button>
        </CardContent></Card>
      ))}
    </div>
  );
};

/** Admin → Garbhsanskar: content, participants, Samvad sessions and broadcasts. */
export const GarbhaTab = () => (
  <Tabs defaultValue="content" data-testid="garbha-admin">
    <TabsList className="bg-muted flex-wrap h-auto">
      <TabsTrigger value="content" data-testid="garbha-sub-content">Content</TabsTrigger>
      <TabsTrigger value="participants" data-testid="garbha-sub-participants">Participants</TabsTrigger>
      <TabsTrigger value="samvad" data-testid="garbha-sub-samvad">Samvad</TabsTrigger>
      <TabsTrigger value="messages" data-testid="garbha-sub-messages">Messages</TabsTrigger>
    </TabsList>
    <TabsContent value="content" className="mt-6"><ContentEditor /></TabsContent>
    <TabsContent value="participants" className="mt-6"><Participants /></TabsContent>
    <TabsContent value="samvad" className="mt-6"><Scheduler kind="samvad" /></TabsContent>
    <TabsContent value="messages" className="mt-6"><Scheduler kind="messages" /></TabsContent>
  </Tabs>
);

export default GarbhaTab;
